import { api, API } from "./api";

/**
 * Chunked uploader — bypasses ingress body-size limits.
 *
 * @param {File} file
 * @param {(pct: number, phase: string) => void} onProgress — pct is 0..1
 * @returns {Promise<{url_path: string, filename: string, size: number}>}
 */
export async function chunkedUpload(file, onProgress = () => {}) {
  if (!file) throw new Error("No file");
  const CHUNK_SIZE = 512 * 1024; // 512KB — well under typical ingress 1MB cap

  onProgress(0, "Initializing…");
  const { data: init } = await api.post("/uploads/init", {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    total_bytes: file.size,
  });
  const uploadId = init.upload_id;
  const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  const token = localStorage.getItem("kf_token");

  for (let i = 0; i < total; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(file.size, start + CHUNK_SIZE);
    const blob = file.slice(start, end);
    // axios serializes Blob as multipart by default; use raw fetch so backend receives octet-stream
    const res = await fetch(`${API}/uploads/${uploadId}/chunk`, {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "X-Chunk-Index": String(i),
        "X-Total-Chunks": String(total),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: blob,
    });
    if (!res.ok) {
      let detail = `Chunk ${i + 1}/${total} failed (${res.status})`;
      try { const j = await res.json(); if (j.detail) detail = j.detail; } catch { /* noop */ }
      throw new Error(detail);
    }
    // Report progress after each chunk (cap at 0.95 so finalize can claim the last bit)
    const pct = Math.min(0.95, (i + 1) / total);
    onProgress(pct, `Uploading… ${i + 1}/${total}`);
  }

  onProgress(0.97, "Finalizing…");
  const { data: final } = await api.post(`/uploads/${uploadId}/finalize`);
  onProgress(1, "Done");
  return final; // { upload_id, filename, url_path, size }
}

/** Resolve a stored upload `url_path` to an absolute URL (handles legacy data: URLs untouched). */
export const resolveUploadUrl = (val) => {
  if (!val) return "";
  if (val.startsWith("data:") || val.startsWith("http")) return val;
  return `${process.env.REACT_APP_BACKEND_URL}${val}`;
};
